/**
 * Cell formatting for a patients-table row.
 *
 * Two tables render the same row shape: the patients screen and the top-bar patient
 * search results (whose hits come back through the backend's `enrich_patient_row`,
 * so they carry the same fields). These formatters live here so the two cannot drift
 * apart - a patient reads the same in both places.
 *
 * Everything here is pure. Cells that open a modal stay in the screen that owns the
 * modal; this file only turns raw patient fields into display text.
 */

export const formatDate = (timestamp: number | undefined | null): string => {
    if (!timestamp || timestamp === 0) return "";
    return new Date(timestamp).toLocaleDateString("en-GB");
};

export const calculateFinishReason = (patient: any): string => {
    const { overide_status, cancel_reason, status } = patient;

    if (overide_status === 'Finish' && cancel_reason === 'Clinic decision') {
        return 'Manual - Clinic decision';
    }
    if (overide_status === 'Finish' && cancel_reason === 'Patient decision') {
        return 'Manual - Patient decision';
    }
    if (overide_status === '' && status === 'Finish') {
        return 'Auto - Finish follow ups';
    }
    if (overide_status === 'Finish' && cancel_reason === 'Auto') {
        return 'Auto - Finish Invitations';
    }
    if ((overide_status === 'Finish' || overide_status === 'Cancel') && cancel_reason === '') {
        return 'Manual';
    }
    return '';
};

export const formatInvitationStatus = (patient: any): string => {
    if (patient.next_meeting > 0) {
        return "next meeting scheduled";
    }
    return patient.invitation_status || "";
};

export const formatTasksNumber = (patient: any): string | number => {
    try {
        const issues = patient.issues ? JSON.parse(patient.issues) : [];
        if (issues.length === 0) return "";
        if (issues.length === 1) return issues[0].issue || "";
        return issues.length;
    } catch {
        return patient.tasks?.length || "";
    }
};

export const formatNextMeetingDateTime = (patient: any): string => {
    const nextMeetingStart = patient.next_meeting_start;

    if (!nextMeetingStart || nextMeetingStart === 0) {
        return "";
    }

    const meetingDate = new Date(nextMeetingStart);
    const now = new Date();

    // Get start and end of today
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayEnd = new Date(todayStart.getTime() + 24 * 60 * 60 * 1000);

    // Get start and end of tomorrow
    const tomorrowStart = new Date(todayEnd);
    const tomorrowEnd = new Date(tomorrowStart.getTime() + 24 * 60 * 60 * 1000);

    const formattedTime = meetingDate.toLocaleTimeString("en-GB", {
        hour: '2-digit',
        minute: '2-digit'
    });

    if (meetingDate >= todayStart && meetingDate < todayEnd) {
        return `Today at ${formattedTime}`;
    } else if (meetingDate >= tomorrowStart && meetingDate < tomorrowEnd) {
        return `Tomorrow at ${formattedTime}`;
    } else {
        const day = String(meetingDate.getDate()).padStart(2, '0');
        const month = String(meetingDate.getMonth() + 1).padStart(2, '0');
        const year = meetingDate.getFullYear();
        return `${day}/${month}/${year} ${formattedTime}`;
    }
};

export const formatMeetingCount = (patient: any): string => {
    // Use pre-calculated meeting_count_total_text from API
    return patient.meeting_count_total_text || "";
};

export const getAgeGroupColor = (patientType: string): string => {
    switch (patientType) {
        case 'Soldier': return '#442586';
        case 'Adult': return '#664ED1';
        case 'Kids': return '#CFC6F9';
        default: return '#D8A5E5';
    }
};

export const formatSubClinic = (patient: any): string => {
    if (patient.clinic_id === 3) {
        return patient.sub_clinic || "Macabi";
    }
    return patient.sub_clinic || "";
};

export const shouldHighlightSubClinic = (patient: any): boolean => {
    return patient.clinic_id === 3 && !patient.obligation_verified;
};

export const formatNotes = (notes: string): string => {
    return notes && notes.trim() !== '' ? notes : 'Empty';
};

export const formatMoveType = (moveType: string | undefined): string => {
    if (!moveType) return "";
    // Convert to Title Case (startCase equivalent)
    return moveType
        .replace(/([a-z])([A-Z])/g, '$1 $2') // Handle camelCase
        .replace(/[_-]/g, ' ') // Replace underscores and hyphens with spaces
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
};

/**
 * Every column the patients table can show, in its default order. The search results
 * table offers the same set through its own Manage Columns dialog, which is why this
 * list is shared rather than declared per screen.
 *
 * `visible` is the patients-table default. The search table keeps its own default
 * visibility (see SEARCH_DEFAULT_VISIBLE_COLUMNS there) because it is a narrower,
 * at-a-glance view.
 */
export const PATIENT_COLUMNS: { id: string; label: string; visible: boolean }[] = [
    { id: "HM_ID", label: "HM ID", visible: true },
    { id: "first_name", label: "First name", visible: true },
    { id: "last_name", label: "Last name", visible: true },
    { id: "status", label: "Status", visible: true },
    { id: "finish_reason", label: "Finish reason", visible: true },
    { id: "invitation_status", label: "Invitation status", visible: true },
    { id: "issues", label: "Issues", visible: true },
    { id: "case_manager_id", label: "Case Manager", visible: true },
    { id: "doctor_id", label: "Psychiatrist", visible: true },
    { id: "clinic_id", label: "Clinic", visible: true },
    { id: "patient_type", label: "Age group", visible: true },
    { id: "sub_clinic", label: "Sub clinic", visible: true },
    { id: "meeting_count", label: "Meeting number", visible: true },
    { id: "notes", label: "Notes", visible: true },
    { id: "last_interaction", label: "Last meeting", visible: true },
    { id: "next_meeting_start", label: "Next meeting", visible: true },
    { id: "funding_entity", label: "Funding entity", visible: true },
    { id: "created_at", label: "Created date", visible: true },
    { id: "move_clinic_status", label: "Move clinic...", visible: true },
    { id: "move_type", label: "Move type", visible: true },
    { id: "payment_link", label: "Payment link", visible: true },
    { id: "area", label: "Area", visible: false },
    { id: "contact_name", label: "Contact Name", visible: false },
    { id: "contact_phone", label: "Contact Phone", visible: false },
    { id: "contact_role", label: "Contact Role", visible: false },
    { id: "cancel_reason", label: "Cancel Reason", visible: false },
    { id: "cancel_notes", label: "Cancel Notes", visible: false },
];
